/**
 * `/connect/slack/callback?code=…&state=…` — where Slack sends a customer after
 * they add a genie to their workspace from the owner's install link. Public:
 * the installer usually has no Help Genie account. The signed `state` names
 * the genie; finishing the install binds the workspace to it.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { chatChannelService } from "@/services/agent/ChatChannelService";
import { SlackInstallResult } from "@/types/agent/chat-channels";

const BRAND_GRADIENT = "linear-gradient(135deg, #4E9CFF, #FF4DB8)";
const SLACK_REDIRECT_DELAY_MS = 800;

type InstallState =
  | { status: "installing" }
  | { status: "done"; result: SlackInstallResult }
  | { status: "cancelled" }
  | { status: "failed"; message: string };

export const SlackInstallCallback = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const [install, setInstall] = useState<InstallState>(() =>
    code && state ? { status: "installing" } : { status: "cancelled" },
  );
  // The code is single-use; StrictMode's double effect would spend it twice.
  const started = useRef(false);

  useEffect(() => {
    if (!code || !state || started.current) return;
    started.current = true;

    chatChannelService
      .completeSlackInstall(code, state)
      .then((result) => setInstall({ status: "done", result }))
      .catch((error: Error) =>
        setInstall({ status: "failed", message: error.message || "The install didn't finish. Please try the link again." }),
      );
  }, [code, state]);

  // Straight into the Slack app's DM, where the genie has already said hello. Without
  // the Slack app nothing opens, so the page offers the same DM in the browser.
  const slackAppUrl = install.status === "done" ? install.result.appUrl : null;
  useEffect(() => {
    if (!slackAppUrl) return;
    const timer = window.setTimeout(() => window.location.assign(slackAppUrl), SLACK_REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [slackAppUrl]);

  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center text-center px-6"
      style={{ background: BRAND_GRADIENT }}
    >
      <Helmet>
        <meta name="robots" content="noindex" />
        <title>Add to Slack · Help Genie</title>
      </Helmet>

      {install.status === "installing" && (
        <>
          <Loader2 className="w-10 h-10 text-white animate-spin mb-5" />
          <h1 className="text-white text-xl font-semibold">Adding your genie to Slack&hellip;</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">This only takes a moment.</p>
        </>
      )}

      {install.status === "done" && (
        <>
          <h1 className="text-white text-2xl font-bold">
            {install.result.genieName} is in {install.result.workspaceName || "your Slack"}
          </h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            {install.result.genieName} is waiting for you in Slack. Opening your chat&hellip;
          </p>
          <a
            href={install.result.appUrl}
            className="mt-6 inline-flex items-center min-h-[44px] px-5 py-2.5 rounded-xl bg-white font-semibold text-genie-primary"
          >
            Open Slack app
          </a>
          <a
            href={install.result.webUrl}
            className="mt-3 inline-flex items-center min-h-[44px] px-3 text-sm font-semibold text-white underline underline-offset-4"
          >
            Open in browser instead
          </a>
        </>
      )}

      {install.status === "cancelled" && (
        <>
          <h1 className="text-white text-2xl font-bold">Nothing was added</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            The install was cancelled in Slack. Open the link you were sent again whenever you&rsquo;re ready.
          </p>
        </>
      )}

      {install.status === "failed" && (
        <>
          <h1 className="text-white text-2xl font-bold">We couldn&rsquo;t finish the install</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">{install.message}</p>
        </>
      )}
    </div>
  );
};

export default SlackInstallCallback;
